import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ModuleFrame, { Chip, KV } from '../ModuleFrame';
import { withApp } from '../AppSelector';
import AgentEditor from '../AgentEditor';
import ConceptScreens from '../ConceptScreens';
import { Suggestions } from '../AppOperate';
import { askNova } from '../ConsoleHeader';
import { apiGet, apiPost, isBackendConfigured } from '../../utils/backendApi';
import { updateEntity, friendlyError } from '../../data/entities';

// Lab › Design — what the app IS (decision lab-is-the-studio, 2026-10-14;
// was App Builder's Design tab, decision app-builder-single-spine): name,
// purpose, who uses it; its agents; and three cards — the documents it
// answers from, data & actions, policies — each linking to the
// organization-wide library module that owns the records. `card` scrolls
// to one of them (old ?tab=knowledge|data|policies links).

const GUARDRAIL_LABELS = {
  approvedSourcesOnly: 'Answer only from this organization\'s published documents',
  citationsRequired: 'Refuse answers that cannot cite a source',
  sensitiveDataMasking: 'Mask personal data (emails, phones, cards) and decline lookups of it',
  uncertainToReview: 'Send uncertain answers to Human Review',
};

export default function Design({ org, app, studio, onPhase, card = null }) {
  const { user, ops, attached, unattached, kDocs, kLive, integs, pipelineTo, can, canEdit, canAgents, attach, detach } = studio;
  useEffect(() => {
    if (!card) return undefined;
    // The cards render after the records load; retry until the anchor exists.
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(`ab-${card}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (el || ++tries > 40) clearInterval(t);
    }, 100);
    return () => clearInterval(t);
  }, [card]);
  return (
    <div className="lab-studio" data-nova-note={`Design phase for "${app.name}": ${attached.length} agent${attached.length === 1 ? '' : 's'} attached, ${kLive.length} live knowledge docs, ${integs.length} integrations${app.links?.pipelineId ? ', workflow linked' : ', no workflow linked'}. NoVA: create_agent, create_knowledgeDoc, build_pipeline, update_app (links).`}>
      <ConceptScreens org={org} appId={app.id} variant="board" canRevise={can('lab.run')} onOpen={(screen, o) => onPhase('concept', null, { fig: 'screens', screen, revise: o?.revise ? 1 : null })} />
      <ModuleFrame.Columns cols={2}>
        <ModuleFrame.Panel title="What the app is" meta="name, purpose, who uses it">
          <DefineForm orgId={org.id} app={app} canEdit={canEdit} />
        </ModuleFrame.Panel>
        <ModuleFrame.Panel
          title="Its agents"
          meta={attached.length ? `${attached.length} attached — each can also live on other apps and websites` : 'no agent yet — the app cannot answer anyone'}
          actions={<AttachAgentMenu org={org} app={app} canEdit={canAgents} unattached={unattached} onAttach={attach} />}
        >
          {!attached.length && (
            <div className="ab-nova-path">
              <p className="mf-muted" style={{ margin: 0 }}>
                An agent is what your users talk to: a greeting, instructions, the documents it answers from, and the records it may look up.
                {canAgents ? ' Create one for this app, pick a role from the library, or attach an agent you already have.' : ' Someone with the “create an agent” permission can create one; NoVA can draft it for you.'}
              </p>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => askNova(`Design the agent for my app "${app.name}" (goal: ${app.goal || 'unset'}; users: ${app.users || 'unset'}). Propose its name, greeting and instructions, then create it and attach it to the app.`)}>◈ Design it with NoVA</button>
            </div>
          )}
          <div style={{ display: 'grid', gap: 12 }}>
            {attached.map((a) => (
              <AgentEditor key={a.id} org={org} agent={a} canEdit={can('agent.update')} docs={kDocs} appDocIds={kDocs.map((d) => d.id)} showInstall={false} onDetach={() => detach(a.id)} defaultOpen={attached.length === 1} />
            ))}
          </div>
          {attached.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Suggestions org={org} app={app} data={ops.data} uid={user.uid} only={['design']} onTab={studio.setTab} compact onChanged={ops.reload} />
            </div>
          )}
        </ModuleFrame.Panel>
      </ModuleFrame.Columns>

      <ModuleFrame.Columns cols={3}>
        <ModuleFrame.Panel
          title="Documents it answers from"
          meta={`${kLive.length} live / ${kDocs.length} linked · library: Knowledge Platform`}
          actions={(
            <>
              <Link to={withApp(`/orgs/${org.id}/knowledge`, app)} className="cs-btn cs-btn-primary cs-btn-sm">Add documents</Link>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => askNova(`Add the documents I attach to my app "${app.name}" as knowledge.`, { attach: true })}>◈ Let NoVA add them</button>
            </>
          )}
        >
          <div id="ab-knowledge" />
          <ul className="mf-list">
            {kDocs.slice(0, 6).map((d) => {
              const boundBy = attached.filter((a) => (a.knowledge?.mode || 'all') === 'all' || (a.knowledge?.mode === 'docs' && (a.knowledge.docIds || []).includes(d.id)) || (a.knowledge?.mode === 'tags' && (a.knowledge.tags || []).some((t) => (d.tags || []).includes(t))));
              return (
                <li key={d.id} className="mf-row">
                  <Chip tone={d.status === 'live' ? 'ok' : 'muted'}>{d.status}</Chip>
                  <span className="mf-row-grow">
                    {d.title}
                    <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>
                      {d.audience === 'public' ? 'public — visitors get answers from it' : 'internal — members only'}
                      {attached.length > 0 && ` · ${boundBy.length ? boundBy.map((a) => a.name).join(', ') : 'no attached agent uses it'}`}
                    </span>
                  </span>
                </li>
              );
            })}
            {kDocs.length > 6 && <li className="mf-row"><Link to={withApp(`/orgs/${org.id}/knowledge`, app)} className="mf-muted">{kDocs.length - 6} more in Knowledge Platform →</Link></li>}
            {!kDocs.length && <li className="cs-help">No documents linked yet. Add PDFs, Word files, text or a web page in Knowledge Platform, linked to this app.</li>}
          </ul>
          <p className="cs-help" style={{ marginTop: 8 }}>
            <strong>Drafts do not answer.</strong> A document answers once it is approved and published (Knowledge Platform → Versions &amp; approvals). <em>Public</em> documents answer visitors; <em>internal</em> ones answer members. Each agent picks its documents under “What it answers from”.
          </p>
          {(ops.data?.suggestions || []).some((x) => x.action?.tab === 'knowledge') && (
            <>
              <h3 className="ab-sugg-h">Asked, but the documents could not answer</h3>
              <Suggestions org={org} app={app} data={ops.data} uid={user.uid} only={['knowledge']} onTab={studio.setTab} compact onChanged={ops.reload} />
            </>
          )}
        </ModuleFrame.Panel>

        <ModuleFrame.Panel title="Data & actions" meta={`${integs.length} integration${integs.length === 1 ? '' : 's'} · libraries: Listen, nQube, Integrations`}>
          <div id="ab-data" />
          {!attached.length && <p className="cs-help" style={{ marginTop: 0 }}>Attach an agent first — what it may look up or propose is set per agent.</p>}
          <ul className="mf-list">
            {attached.map((a) => (
              <li key={a.id} className="mf-row">
                <span className="mf-row-grow">
                  <strong>{a.name}</strong>
                  <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>
                    looks up: {(a.allowReads || []).join(', ') || 'nothing'} · may propose: {(a.allowWrites || []).join(', ') || 'nothing'} (a person confirms)
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <KV rows={[
            ['Integrations', integs.length ? integs.map((i) => `${i.name}${i.lastTest?.ok ? ' ✓' : ''}`).join(', ') : 'none'],
            ['Data sources', app.links?.dataSourceIds?.length ? `${app.links.dataSourceIds.length} linked` : 'none'],
            ['GraphQL', app.links?.graphqlReady ? 'ready' : 'not set up'],
            ['Workflow', app.links?.pipelineId ? <Link to={pipelineTo}>open canvas</Link> : 'none'],
          ]} />
          <div className="ab-next-actions" style={{ marginTop: 10 }}>
            <Link to={withApp(`/orgs/${org.id}/integrations`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Integrations</Link>
            <Link to={withApp(`/orgs/${org.id}/data`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Listen</Link>
            <Link to={pipelineTo} className="cs-btn cs-btn-outline cs-btn-sm">Workflow</Link>
          </div>
        </ModuleFrame.Panel>

        <ModuleFrame.Panel title="Policies" meta="organization-wide — apply to every agent and app" actions={<Link to={withApp(`/orgs/${org.id}/knowledge?tab=guardrails`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Change</Link>}>
          <div id="ab-policies" />
          <ul className="mf-list">
            {Object.entries(GUARDRAIL_LABELS).map(([k, label]) => (
              <li key={k} className="mf-row">
                <Chip tone={org.guardrails?.[k] ? 'ok' : 'muted'}>{org.guardrails?.[k] ? 'on' : 'off'}</Chip>
                <span className="mf-row-grow">{label}</span>
              </li>
            ))}
          </ul>
          <p className="mf-muted" style={{ margin: '10px 0 0' }}>
            {org.reviewRouting?.rules?.length
              ? `${org.reviewRouting.rules.length} routing rule${org.reviewRouting.rules.length === 1 ? '' : 's'} decide who gets uncertain answers, policy refusals and handoffs.`
              : 'No routing rules yet — uncertain answers and handoffs land in the default queue for any admin.'}
            {' '}<Link to={withApp(`/orgs/${org.id}/human-review`, app)}>Human Review →</Link>
          </p>
          <p className="cs-help" style={{ marginTop: 8 }}>Admins change these in Knowledge Platform → Guardrails and in Human Review.</p>
        </ModuleFrame.Panel>
      </ModuleFrame.Columns>
    </div>
  );
}

// Attach an existing agent, create a starter one for this app, or add a
// library role (both create paths are admin: agents are admin-created).
function AttachAgentMenu({ org, app, canEdit, unattached, onAttach }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [library, setLibrary] = useState([]);
  useEffect(() => {
    if (!canEdit || !isBackendConfigured()) return undefined;
    let alive = true;
    apiGet('/agent-library').then((d) => { if (alive) setLibrary(d.roles || []); }).catch(() => {});
    return () => { alive = false; };
  }, [canEdit]);
  const run = async (what, fn) => {
    setBusy(what); setErr('');
    try {
      await fn();
    } catch (ex) {
      setErr(friendlyError(ex));
    } finally {
      setBusy('');
    }
  };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {canEdit && isBackendConfigured() && (
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!!busy} onClick={() => run('new', async () => { await apiPost(`/apps/${org.id}/${app.id}/agent`, {}); })}>
          {busy === 'new' ? 'Creating…' : '+ Create agent for this app'}
        </button>
      )}
      {canEdit && library.length > 0 && (
        <select className="cs-input" aria-label="Add from library" value="" disabled={!!busy} onChange={(e) => { const role = e.target.value; if (role) run('lib', async () => { const r = await apiPost(`/agent-library/${role}`, { orgId: org.id, workspaceId: app?.workspaceId || null }); await onAttach(r.id); }); }}>
          <option value="">From library…</option>
          {library.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      )}
      {unattached.length > 0 && (
        <select className="cs-input" aria-label="Attach existing agent" value="" disabled={!!busy} onChange={(e) => { const id = e.target.value; if (id) run('attach', () => onAttach(id)); }}>
          <option value="">Attach existing…</option>
          {unattached.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      )}
      {err && <span className="cs-form-error">{err}</span>}
    </div>
  );
}

function DefineForm({ orgId, app, canEdit }) {
  const [form, setForm] = useState({ name: app.name || '', goal: app.goal || '', users: app.users || '' });
  const [state, setState] = useState('');
  useEffect(() => {
    setForm({ name: app.name || '', goal: app.goal || '', users: app.users || '' });
  }, [app.id, app.name, app.goal, app.users]);
  const dirty = form.name !== (app.name || '') || form.goal !== (app.goal || '') || form.users !== (app.users || '');
  const save = async () => {
    setState('saving');
    try {
      await updateEntity('app', { org: orgId }, app.id, { name: form.name.trim(), goal: form.goal.trim(), users: form.users.trim() });
      setState('saved');
    } catch (ex) {
      setState(friendlyError(ex));
    }
  };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <label className="mf-muted">Name<input className="cs-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canEdit} /></label>
      <label className="mf-muted">What it must do, for whom<textarea className="mf-textarea" style={{ minHeight: 80 }} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} disabled={!canEdit} /></label>
      <label className="mf-muted">Who uses it<input className="cs-input" value={form.users} onChange={(e) => setForm({ ...form, users: e.target.value })} placeholder="e.g. support agents, customers on the web" disabled={!canEdit} /></label>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!dirty || state === 'saving'} onClick={save}>{state === 'saving' ? 'Saving…' : 'Save'}</button>
          {state && state !== 'saving' && <span className={state === 'saved' ? 'mf-muted' : 'cs-form-error'}>{state === 'saved' ? 'Saved.' : state}</span>}
        </div>
      )}
      <p className="cs-help" style={{ margin: 0 }}>The purpose feeds the agent's draft instructions and NoVA's plans — keep it concrete.</p>
    </div>
  );
}
